import { useAssetStore } from './asset'
import { useCrmStore } from './crm'
import { useSessionStore } from './session'
import { useUserSettingsStore } from './settings'
import { useSubmissionStore } from './submission'
import { useSyncStore } from './sync'
import type {
	HubUser,
	PapiResult,
	ProducerNumber,
	Role,
	V3SalesContact,
	V4SalesContact,
	Vendor,
} from '@/apiTypes'
import { useCloudSyncService } from '@/services/CloudSync/cloudSyncService'
import { axiosInstance } from '@/services/axiosInstance'
import { deepToRaw } from '@/services/deepToRaw'
import { Feature, useFeatureService } from '@/services/featureService'
import { mapSalesContact } from '@/services/mapSalesContactService'
import { useContentStore } from '@/stores/content'
import { useDocumentStore } from '@/stores/document'
import type { CombinedUser } from '@/types'
import { captureException, setUser } from '@sentry/vue'
import axios, {
	type AxiosBasicCredentials,
	type AxiosError,
	type AxiosRequestConfig,
} from 'axios'
import localforageLib from 'localforage'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

const CAP_VISIT = 'studio.visit'
const CAP_ADMIN = 'studio.viewAsAny'
const CAP_MANAGER = 'studio.viewAsReport'

/** ID for the hub role that represents a sales user */
const ROLE_SALES_CONTACT = '1AA0B4EC-198B-4F7F-AF6B-B1B75CF6B0AA'

export enum LoginResult {
	Success,
	Unauthorized,
	NoNetwork,
}

const localAuth = localforageLib.createInstance({
	name: 'auth',
})

/** sessionStorage key: whether we have auto-displayed SepioProducerWarning */
const SEPIO_PRODUCER_WARNING_SHOWN_KEY = 'hasAutoShownSepioProducerWarning'

export const useAuthStore = defineStore('auth', () => {
	/** Struct with user data from Hub and Salesforce */
	const user = ref<CombinedUser>()

	const sessionExpired = ref(false)
	const showLogin = ref(false)
	const authToken = ref<string>('')
	const crm = useCrmStore()
	const storyBlokToken = ref<string | undefined>()
	const viewingAsAnother = computed(
		() =>
			user.value?.salesContact &&
			!user.value?.hub.roles.data.some(
				(r) => r.crmUserId === user.value?.salesContact?.id
			)
	)

	/** The user should have access to pick any other user to view the app as */
	const isAdmin = computed(
		() =>
			user.value?.hub &&
			userHasCapability(CAP_VISIT) &&
			userHasCapability(CAP_ADMIN)
	)

	/** The user should be able to view the app as one of their reports */
	const isManager = computed(
		() =>
			user.value?.hub &&
			userHasCapability(CAP_VISIT) &&
			userHasCapability(CAP_MANAGER)
	)

	/** The manager page should be the active screen */
	const showManagerPage = computed(
		() =>
			user.value?.hub &&
			!user.value?.salesContact &&
			!sessionExpired.value &&
			userHasCapability(CAP_VISIT) &&
			userHasCapability(CAP_MANAGER)
	)

	/** The admin page should be the active screen */
	const showAdminPage = computed(
		// True if the user is logged in, does not have an active sales contact,
		// does not have an expired session, and has appropriate capabilities
		() =>
			user.value?.hub &&
			!user.value?.salesContact &&
			!sessionExpired.value &&
			userHasCapability(CAP_VISIT) &&
			userHasCapability(CAP_ADMIN)
	)

	/** Returns the salesContact's name and vendor-specific producer number */
	const salesContactNameAndVendorProducerNumber = computed(() => {
		const salesContact = user.value?.salesContact
		const name =
			`${salesContact?.firstname ?? ''} ${salesContact?.lastname ?? ''}`.trim()
		const producerNumbers = salesContact?.producerNumbers?.data ?? []

		return (vendor: Vendor) => {
			if (!salesContact) return { name: '', producerNumber: '' }
			const producerNumber =
				producerNumbers.find(
					(producerNum: ProducerNumber) => producerNum.vendor === vendor
				)?.producerNumber ?? ''

			return { name, producerNumber }
		}
	})

	// Returns true if the sales contact is authorized to sell Everplans
	const isEverplansAuthorized = computed(() => {
		const salesContact = user.value?.salesContact
		return salesContact?.isEverplansAuthorized ?? false
	})

	/** True if we are currently unable to validate user with server */
	const usingStoredAuth = ref(false)

	function userHasCapability(capName: string) {
		return user.value?.hub.capabilities.data.includes(capName)
	}

	/**
	 * Fetch the user record from the server.
	 * This represents authentication and setting up the user.
	 */
	async function getUserFromServer(
		auth?: AxiosBasicCredentials
	): Promise<LoginResult> {
		try {
			// Authenticate the user w/ PAPI
			const userRequestConfig: AxiosRequestConfig = {
				params: {
					include: 'roles,capabilities',
				},
			}
			if (auth) userRequestConfig.auth = auth
			const userResponse = await axiosInstance.get<HubUser>(
				'/v2/user',
				userRequestConfig
			)

			let combinedUser: CombinedUser = {
				salesContact: user.value?.salesContact,
				hub: userResponse.data,
			}

			user.value = combinedUser

			if (!userHasCapability('studio.visit')) {
				throw new Error('User does not have permissions to visit the app')
			}

			// User is now a real Hub user who is allowed to be here.
			setUser({ id: userResponse.data.id, email: userResponse.data.email })
			await localAuth.setItem('user', deepToRaw(combinedUser))

			sessionExpired.value = false
			usingStoredAuth.value = false

			// If they are a manager or admin, wait for them to pick a sales contact
			if (userHasCapability(CAP_MANAGER) || userHasCapability(CAP_ADMIN)) {
				return LoginResult.Success
			}

			// Find associated sales contact record
			const salesContactRole: Role | undefined =
				userResponse.data.roles?.data.find(
					(role: Role) => role.roleId == ROLE_SALES_CONTACT
				)

			if (!salesContactRole || !salesContactRole.crmUserId) {
				console.warn('User does not have a sales contact role')
				return LoginResult.Success
			}

			// Fetch and use user's own sales contact
			await viewAppAsSalesContact(salesContactRole.crmUserId)
			return LoginResult.Success
		} catch (error) {
			if (axios.isAxiosError(error)) {
				if ((error as AxiosError).response?.status == 401) {
					if (auth?.password && auth?.username) {
						throw new Error('Username and password are not valid.')
					}

					console.warn('You are not logged in')
					throw new Error('You are not logged in')
				}

				if ((error as AxiosError).code == 'ERR_NETWORK') {
					console.error("Can't reach the login server")
					throw new Error("Can't reach the login server.")
				}
			}

			captureException(error, {
				extra: {
					username: auth?.username,
				},
			})

			throw error
		}
	}

	/** Impersonate the given sales contact */
	async function viewAppAsSalesContact(salesContactId: string) {
		if (!user.value) throw new Error('Not logged in')

		const isUsingSfIntegration = useFeatureService().isEnabled(
			Feature.SalesforceIntegration
		)

		let V3SalesContactData

		if (!isUsingSfIntegration) {
			V3SalesContactData = await getV3SalesContact(salesContactId)
			crm.setAssignedV3FuneralHomes(V3SalesContactData.funeralHomes?.data)
		}

		const V4SalesContactData = await getV4SalesContact(
			isUsingSfIntegration ? salesContactId : V3SalesContactData?.salesforceId!
		)

		// todo PSS-1665 set funeral home data in crm.ts

		const universalSalesContact = mapSalesContact(
			V4SalesContactData,
			V3SalesContactData
		)

		const combinedUser: CombinedUser = {
			salesContact: {
				...universalSalesContact,
			},
			hub: user.value.hub,
		}

		user.value = combinedUser
		await localAuth.setItem('user', deepToRaw(combinedUser))
		await useUserSettingsStore().loadSettings()

		// Allow the home Sepio warning to auto-open once for this sales contact
		clearSepioProducerWarningAutoShown()

		useSyncStore().beginSync()
	}

	/** Fetch AFP data from old CRM */
	async function getV3SalesContact(salesContactId: string) {
		// Get sales contact and associated funeral homes
		const contactResponse = await axiosInstance.get<PapiResult<V3SalesContact>>(
			'/v3/counselors/' + salesContactId + '?include=funeralHomes'
		)

		return contactResponse.data.data
	}

	/** Fetch AFP data from Salesforce */
	async function getV4SalesContact(salesContactId: string) {
		// Get sales contact, associated funeral homes, active producer numbers, and licenses
		const v4ContactResponse = await axiosInstance.get<
			PapiResult<V4SalesContact>
		>(
			'/v4/contacts/' +
				salesContactId +
				'?include=producerNumbers:scope(active),licenses,afpFuneralHomes'
		)

		return v4ContactResponse.data.data
	}

	/** As manager or admin, go back to manager/admin screen */
	async function undoViewAppAs() {
		if (!user.value) throw new Error('Not logged in; cannot undoViewAppAs')

		// trigger all debounces to send
		await useCloudSyncService().flushPendingUpserts()
		// clear out local data
		await purgeSalesContactData()

		const combinedUser: CombinedUser = {
			salesContact: undefined,
			hub: user.value?.hub,
		}

		user.value = combinedUser
		await localAuth.setItem('user', deepToRaw(combinedUser))
		await useSubmissionStore().clearAllRequestQueueStores()

		// Reload the app to clear out any stale data and URL params
		window.location.replace(import.meta.env.VITE_APP_ROOT)
	}

	/** Load the user record from local storage */
	async function getUserFromLocal(): Promise<void> {
		const savedUser = await localAuth.getItem<CombinedUser>('user')
		if (!savedUser) {
			throw new Error('No user found in local storage')
		}

		if (!user.value) {
			user.value = savedUser
			usingStoredAuth.value = true
		}
	}

	/**
	 * Get the id for the sales contact that represents the logged-in user, or undefined.
	 * Not every user will have this.
	 */
	function getOwnSalesContactId(): string | undefined {
		return (
			user.value?.hub.roles.data.find(
				(role) => role.roleId == ROLE_SALES_CONTACT
			)?.crmUserId || undefined
		)
	}

	/**
	 * Gets the Salesforce Sales Contact Id of the user you are logged in as
	 */
	function getActiveSalesContactId(): string | undefined {
		if (useFeatureService().isEnabled(Feature.SalesforceIntegration)) {
			return user.value?.salesContact?.id
		}
		return user.value?.salesContact?.salesforceId
	}

	/**
	 * Check that an AFP has Sepio Guard number, which means they can sell Sepio
	 * But missing the Sepio Guard API number, which means they should see a warning
	 * */
	function missingSepioGuardApiProducerNumber() {
		const producerNumbers = user.value?.salesContact?.producerNumbers?.data
		if (!producerNumbers) {
			return false
		}

		const hasSepioGuardApiNumber = producerNumbers.some(
			(item: ProducerNumber) => item.vendor === 'Sepio Guard API'
		)
		const hasSepioGuardNumber = producerNumbers.some(
			(item: ProducerNumber) => item.vendor === 'Sepio Guard'
		)

		return hasSepioGuardNumber && !hasSepioGuardApiNumber
	}

	/** Whether the home dashboard already auto-opened the Sepio warning for this sales contact */
	function hasAutoShownSepioProducerWarningOnHome(): boolean {
		return !!sessionStorage.getItem(SEPIO_PRODUCER_WARNING_SHOWN_KEY)
	}

	function markSepioProducerWarningShownOnHome() {
		sessionStorage.setItem(SEPIO_PRODUCER_WARNING_SHOWN_KEY, 'true')
	}

	function clearSepioProducerWarningAutoShown() {
		sessionStorage.removeItem(SEPIO_PRODUCER_WARNING_SHOWN_KEY)
	}

	/** Remove all data specific to the sales contact */
	async function purgeSalesContactData() {
		await Promise.all([
			useCrmStore().purge(),
			useContentStore().purge(),
			useSessionStore().purge(),
			useCloudSyncService().purge(),
			useDocumentStore().purge(),
		])
	}

	/** Log out of the system and delete all local data */
	async function logOut(): Promise<void> {
		clearSepioProducerWarningAutoShown()
		await Promise.all([
			purgeSalesContactData(),
			localAuth.clear(),
			useSubmissionStore().clearAllRequestQueueStores(),
			useAssetStore().purge(),
			// todo fix cors error on logout
			axiosInstance.get('/logout', { maxRedirects: 0 }),
		]).finally(() => {
			sessionExpired.value = false
			window.location.replace(import.meta.env.VITE_APP_ROOT)
		})
	}

	async function getLoginEmail() {
		const result = await localAuth.getItem<string>('userLoginEmail')
		return result || ''
	}

	async function setLoginEmail(email: string) {
		const result = await localAuth.setItem('userLoginEmail', email)
		return result || ''
	}

	/** Set the user's access token for their login session */
	function setAuthToken(token: string) {
		authToken.value = token
		return localAuth.setItem('papiAuthToken', token)
	}

	/** Get the user's access token for their login session */
	async function getAuthToken() {
		if (authToken.value) return authToken.value

		const token = await localAuth.getItem<string>('papiAuthToken')

		if (token) {
			authToken.value = token
			return token
		}

		return null
	}

	/** Get the Storyblok token from the app API */
	async function getStoryBlokToken(): Promise<string | undefined> {
		if (storyBlokToken.value) return storyBlokToken.value
		let body

		// Specifically using fetch to get around axios interceptors
		let response = await fetch(
			`${import.meta.env.VITE_SALES_STUDIO_API_URL}/token?pUserToken=${authToken.value}`,
			{
				credentials: 'include',
			}
		)
		if (response.status === 401) {
			sessionExpired.value = true
			throw 'Unable to log into papi'
		}

		body = await response.json()

		storyBlokToken.value = body.storyBlokToken
		return storyBlokToken.value
	}

	const presentationLicenses = computed(() => {
		return user.value?.salesContact?.presentationLicenses ?? []
	})

	return {
		user,
		showLogin,
		sessionExpired,
		isAdmin,
		isEverplansAuthorized,
		isManager,
		salesContactNameAndVendorProducerNumber,
		logOut,
		usingStoredAuth,
		getUserFromLocal,
		getUserFromServer,
		getLoginEmail,
		setLoginEmail,
		getOwnSalesContactId,
		getActiveSalesContactId,
		getAuthToken,
		showAdminPage,
		showManagerPage,
		setAuthToken,
		getStoryBlokToken,
		undoViewAppAs,
		viewAppAsSalesContact,
		viewingAsAnother,
		presentationLicenses,
		missingSepioGuardApiProducerNumber,
		hasAutoShownSepioProducerWarningOnHome,
		markSepioProducerWarningShownOnHome,
	}
})
